import { useEffect, useState } from 'react';
import { api } from '../api.js';

const MAX_LINKS = 6;

/**
 * Adding and removing the links on your own profile.
 *
 * Links save on their own rather than with the rest of the form, because the
 * server has to go and read each one and there is no sensible way to make
 * "Save profile" wait for somebody else's website. A row appears the moment it
 * is added and fills itself in.
 *
 * The list itself is owned above: reading a link produces tags the interest
 * editor offers, so the two have to be looking at the same fetch.
 */
export default function LinkEditor({ links = [], loaded = false, onReload }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Something added a moment ago is still being read. Ask again rather than
  // leave "Reading that link…" sitting there until the modal is reopened.
  const waiting = links.some((link) => link.state === 'pending');
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(() => { onReload?.(); }, 2500);
    return () => clearTimeout(timer);
  }, [waiting, links, onReload]);

  async function add(event) {
    event.preventDefault();
    const url = draft.trim();
    if (!url || busy) return;
    setBusy(true);
    setError('');
    try {
      await api.addLink(url);
      setDraft('');
      await onReload?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    try {
      await api.removeLink(id);
      await onReload?.();
    } catch (err) {
      setError(err.message);
    }
  }

  const full = links.length >= MAX_LINKS;

  return (
    <div className="link-editor">
      {links.length > 0 && (
        <div className="link-rows">
          {links.map((link) => (
            <div className="link-row" key={link.id}>
              <span className="link-row-main">
                <span className="link-row-title">
                  {link.kind === 'account' ? link.handle
                    : link.state === 'ok' && link.title ? link.title
                    : link.host}
                </span>
                <span className="link-row-note">
                  {link.kind === 'account' ? `${link.service} · ${link.host}`
                    : link.state === 'pending' ? 'Reading that link…'
                    : link.state === 'ok' ? link.host
                    : link.state === 'refused' ? `${link.host} · that link cannot be read`
                    : `${link.host} · nothing to show yet`}
                </span>
              </span>
              <button
                type="button" className="icon-btn" onClick={() => remove(link.id)}
                title="Remove this link" aria-label={`Remove ${link.host}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {loaded && !full && (
        // Not a nested <form>: this sits inside the profile form, and a form
        // inside a form is invalid HTML that submits the wrong one.
        <div className="link-add">
          <input
            value={draft}
            placeholder="e.g. github.com/you"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label="Add a link"
            onChange={(e) => { setDraft(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') add(e); }}
          />
          <button type="button" className="btn btn-sm" onClick={add} disabled={!draft.trim() || busy}>
            Add
          </button>
        </div>
      )}
      {full && <p className="hint">That is the {MAX_LINKS} a profile holds.</p>}
      {error && <p className="hint link-error">{error}</p>}
    </div>
  );
}
